import React from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const COPY = {
  en: {
    eyebrow: "The team",
    title: "The people behind Titan Infra",
    lead:
      "A senior, cross-continental team combining finance, infrastructure engineering and on-the-ground presence in Batam. Builders — not bankers.",
    foundingTeam: "Founding team",
    disclaimer:
      "Team profiles are indicative of the current founding team. Additional hires and technical partners will be announced as they are confirmed.",
    members: [
      {
        name: "Doudou David",
        role: "CEO — Strategy Intelligence Solutions",
        location: "London, United Kingdom",
        bio:
          "Multi-entrepreneur. 25 years of experience in finance. Specialist in Financial Intelligence. Structures the investment vehicle, oversees capital allocation and investor relations for Titan Infra.",
        img: "/team/david-doudou.png",
      },
      {
        name: "Patrick Willems",
        role: "Business Development Engineer — AI Infrastructure & Data Centers",
        location: "Brussels, Belgium",
        bio:
          "12+ years bridging commercial and engineering teams across European data-center operators. Drives partnerships with GPU vendors, colocation providers and enterprise AI customers. Handles the technical pre-sales of Titan compute capacity.",
        img:
          "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=800&q=80",
      },
      {
        name: "Ayu Wijaya",
        role: "Head of Indonesia — Europe Operations",
        location: "Batam, Indonesia",
        bio:
          "Born and raised in Batam. 10 years operating between ASEAN industrial parks and European partners. Manages relations with the Batam Free Zone Authority (BP Batam), BKPM and local suppliers. Single point of contact for anything happening on the ground in Indonesia.",
        img:
          "/team/ayu-wijaya.png",
      },
      {
        name: "Wei Lin",
        role: "Chief Technology Officer",
        location: "Singapore",
        bio:
          "18 years in high-density data-center architecture across APAC. Deployed 100+ MW of AI-ready capacity in Singapore, Japan and Indonesia. Expert in liquid cooling, GPU cluster orchestration and Tier-III/IV compliance. Owns the technical roadmap of Titan Infra.",
        img:
          "/team/wei-lin.png",
      },
    ],
  },
  fr: {
    eyebrow: "Confidentiel · Accès investisseurs uniquement",
    title: "Les personnes derrière Titan Infra",
    lead:
      "Une équipe senior, intercontinentale, alliant finance, ingénierie d'infrastructure et présence terrain à Batam. Cette page est réservée à nos investisseurs souscripteurs — merci de ne pas la diffuser.",
    foundingTeam: "Équipe fondatrice",
    disclaimer:
      "Les profils sont partagés à titre confidentiel avec les investisseurs souscripteurs. Merci de ne pas les redistribuer.",
    members: [
      {
        name: "Doudou David",
        role: "CEO — Strategy Intelligence Solutions",
        location: "Londres, Royaume-Uni",
        bio:
          "Multi-entrepreneur. 25 ans d'expérience en finance. Spécialiste en Financial Intelligence. Structure le véhicule d'investissement, supervise l'allocation du capital et les relations investisseurs de Titan Infra.",
        img:
          "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=800&q=80",
      },
      {
        name: "Patrick Willems",
        role: "Business Development Engineer — Infrastructures IA & Data Centers",
        location: "Bruxelles, Belgique",
        bio:
          "12+ ans à interfacer les équipes commerciales et techniques d'opérateurs data-center européens. Pilote les partenariats avec les fournisseurs GPU, les acteurs de colocation et les clients IA en entreprise. Gère l'avant-vente technique de la capacité de calcul Titan.",
        img:
          "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=800&q=80",
      },
      {
        name: "Ayu Wijaya",
        role: "Head of Indonesia — Europe Operations",
        location: "Batam, Indonésie",
        bio:
          "Née et élevée à Batam. 10 ans à opérer entre les parcs industriels ASEAN et les partenaires européens. Gère les relations avec la Batam Free Zone Authority (BP Batam), le BKPM et les fournisseurs locaux. Point de contact unique pour tout ce qui se passe sur le terrain en Indonésie.",
        img:
          "/team/ayu-wijaya.png",
      },
      {
        name: "Wei Lin",
        role: "Chief Technology Officer",
        location: "Singapour",
        bio:
          "18 ans en architecture de data centers haute densité en Asie-Pacifique. Plus de 100 MW de capacité IA déployés à Singapour, au Japon et en Indonésie. Expert en refroidissement liquide, orchestration de clusters GPU et conformité Tier-III/IV. Détient la feuille de route technique de Titan Infra.",
        img:
          "/team/wei-lin.png",
      },
    ],
  },
  es: {
    eyebrow: "Confidencial · Acceso solo para inversores",
    title: "Las personas detrás de Titan Infra",
    lead:
      "Un equipo sénior, intercontinental, que combina finanzas, ingeniería de infraestructura y presencia sobre el terreno en Batam. Esta página está reservada a nuestros inversores suscritos — por favor, no la difunda.",
    foundingTeam: "Equipo fundador",
    disclaimer:
      "Los perfiles se comparten de forma confidencial con los inversores suscritos. No redistribuir.",
    members: [
      {
        name: "Doudou David",
        role: "CEO — Strategy Intelligence Solutions",
        location: "Londres, Reino Unido",
        bio:
          "Multi-emprendedor. 25 años de experiencia en finanzas. Especialista en Financial Intelligence. Estructura el vehículo de inversión, supervisa la asignación de capital y las relaciones con inversores de Titan Infra.",
        img: "/team/david-doudou.png",
      },
      {
        name: "Patrick Willems",
        role: "Business Development Engineer — Infraestructuras IA y Data Centers",
        location: "Bruselas, Bélgica",
        bio:
          "Más de 12 años conectando equipos comerciales y de ingeniería en operadores europeos de data centers. Impulsa asociaciones con proveedores de GPU, colocación y clientes IA empresariales. Gestiona la preventa técnica de la capacidad de cálculo Titan.",
        img:
          "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=800&q=80",
      },
      {
        name: "Ayu Wijaya",
        role: "Head of Indonesia — Europe Operations",
        location: "Batam, Indonesia",
        bio:
          "Nacida y criada en Batam. 10 años operando entre parques industriales ASEAN y socios europeos. Gestiona las relaciones con la Batam Free Zone Authority (BP Batam), el BKPM y proveedores locales. Punto único de contacto para todo lo que sucede sobre el terreno en Indonesia.",
        img:
          "/team/ayu-wijaya.png",
      },
      {
        name: "Wei Lin",
        role: "Chief Technology Officer",
        location: "Singapur",
        bio:
          "18 años en arquitectura de data centers de alta densidad en Asia-Pacífico. Más de 100 MW de capacidad IA desplegados en Singapur, Japón e Indonesia. Experto en refrigeración líquida, orquestación de clústeres GPU y cumplimiento Tier-III/IV. Detenta la hoja de ruta técnica de Titan Infra.",
        img:
          "/team/wei-lin.png",
      },
    ],
  },
};

export default function PortalTeam() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;

  return (
    <div data-testid="portal-team-page" className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-14">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-emerald-400 font-medium mb-6" data-testid="team-eyebrow">
          {c.eyebrow}
        </div>
        <h1 className="font-display font-light tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[1.02]" data-testid="team-title">
          {c.title}
        </h1>
        <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-muted-foreground" data-testid="team-lead">
          {c.lead}
        </p>
      </div>

      {/* Team grid */}
      <p className="overline text-muted-foreground mb-6">{c.foundingTeam}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="team-grid">
        {c.members.map((m, i) => (
          <motion.article
            key={m.name}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: i * 0.08 }}
            data-testid={`team-member-${i}`}
            className="group border border-border bg-card overflow-hidden flex flex-col"
          >
            <div className="aspect-[3/4] overflow-hidden bg-secondary relative">
              <img
                src={m.img}
                alt={m.name}
                className="w-full h-full object-cover object-[center_20%] grayscale group-hover:grayscale-0 transition-all duration-1000 ease-out"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-3 left-4 right-4 flex items-center gap-1.5 text-[11px] text-white/80">
                <MapPin className="w-3 h-3" />
                <span>{m.location}</span>
              </div>
            </div>
            <div className="p-6 flex-1 flex flex-col">
              <p className="font-display text-xl md:text-2xl tracking-tight" data-testid={`team-member-name-${i}`}>
                {m.name}
              </p>
              <p className="mt-1.5 text-[11px] uppercase tracking-widest text-emerald-500/90 font-medium" data-testid={`team-member-role-${i}`}>
                {m.role}
              </p>
              <p className="mt-4 text-[13px] leading-relaxed text-muted-foreground">
                {m.bio}
              </p>
            </div>
          </motion.article>
        ))}
      </div>

      <p className="mt-14 text-[11px] leading-relaxed text-muted-foreground/70 max-w-2xl" data-testid="team-disclaimer">
        {c.disclaimer}
      </p>
    </div>
  );
}
